import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { settingsAPI } from '../../services/settingsService';

// Async thunks
export const fetchUserAccountSettings = createAsyncThunk(
  'settings/fetchUserAccountSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.getUserAccountSettings();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSessionSettings = createAsyncThunk(
  'settings/updateSessionSettings',
  async (lifetime, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.updateSessionSettings(lifetime);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleTwoWayAuth = createAsyncThunk(
  'settings/toggleTwoWayAuth',
  async (status, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.toggleTwoWayAuth(status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const clearCache = createAsyncThunk(
  'settings/clearCache',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.clearCache();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchFollowUpTemplates = createAsyncThunk(
  'settings/fetchFollowUpTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.getFollowUpTemplates();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveFollowUpTemplate = createAsyncThunk(
  'settings/saveFollowUpTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.storeFollowUpTemplate(templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteFollowUpTemplate = createAsyncThunk(
  'settings/deleteFollowUpTemplate',
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.deleteTemplate(templateId);
      return { templateId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTemplateContent = createAsyncThunk(
  'settings/fetchTemplateContent',
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.getTemplateContent(templateId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Calendar Permission async thunks
export const fetchCalendarPermissions = createAsyncThunk(
  'settings/fetchCalendarPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.getCalendarPermissions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCalendarPermission = createAsyncThunk(
  'settings/createCalendarPermission',
  async (permissionData, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.createCalendarPermission(permissionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCalendarPermission = createAsyncThunk(
  'settings/updateCalendarPermission',
  async ({ permissionId, permissionData }, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.updateCalendarPermission(permissionId, permissionData);
      return { permissionId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCalendarPermission = createAsyncThunk(
  'settings/deleteCalendarPermission',
  async (permissionId, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.deleteCalendarPermission(permissionId);
      return { permissionId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCaregiversForPermission = createAsyncThunk(
  'settings/fetchCaregiversForPermission',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsAPI.getCaregiversForPermission();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Account Settings
  userAccount: null,
  accountLoading: false,
  accountError: null,
  
  // Session Settings
  sessionUpdating: false,
  sessionError: null,
  
  // Two-way Auth
  twoWayAuthUpdating: false,
  twoWayAuthError: null,
  
  // Cache
  cacheClearing: false,
  cacheError: null,
  
  // Follow-up Templates
  followUpTemplates: [],
  templatesLoading: false,
  templatesError: null,
  
  // Template Operations
  templateSaving: false,
  templateDeleting: false,
  templateError: null,
  
  // Calendar Permissions
  calendarPermissions: [],
  permissionsLoading: false,
  permissionsError: null,
  permissionSaving: false,
  permissionDeleting: false,
  permissionError: null,
  caregivers: [],
  caregiversLoading: false,
  caregiversError: null,
  
  // UI State
  activeTab: 'account',
  successMessage: null,
  errorMessage: null
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    clearMessages: (state) => {
      state.successMessage = null;
      state.errorMessage = null;
      state.accountError = null;
      state.sessionError = null;
      state.twoWayAuthError = null;
      state.cacheError = null;
      state.templatesError = null;
      state.templateError = null;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
      state.errorMessage = null;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
      state.successMessage = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch User Account Settings
    builder
      .addCase(fetchUserAccountSettings.pending, (state) => {
        state.accountLoading = true;
        state.accountError = null;
      })
      .addCase(fetchUserAccountSettings.fulfilled, (state, action) => {
        state.accountLoading = false;
        state.userAccount = action.payload;
        state.accountError = null;
      })
      .addCase(fetchUserAccountSettings.rejected, (state, action) => {
        state.accountLoading = false;
        state.accountError = action.payload;
      });

    // Update Session Settings
    builder
      .addCase(updateSessionSettings.pending, (state) => {
        state.sessionUpdating = true;
        state.sessionError = null;
      })
      .addCase(updateSessionSettings.fulfilled, (state, action) => {
        state.sessionUpdating = false;
        if (state.userAccount) {
          state.userAccount.session_time = action.payload.session_time;
        }
        state.sessionError = null;
        state.successMessage = action.payload.message;
      })
      .addCase(updateSessionSettings.rejected, (state, action) => {
        state.sessionUpdating = false;
        state.sessionError = action.payload;
        state.errorMessage = action.payload;
      });

    // Toggle Two-way Auth
    builder
      .addCase(toggleTwoWayAuth.pending, (state) => {
        state.twoWayAuthUpdating = true;
        state.twoWayAuthError = null;
      })
      .addCase(toggleTwoWayAuth.fulfilled, (state, action) => {
        state.twoWayAuthUpdating = false;
        if (state.userAccount) {
          state.userAccount.is_two_way_auth = action.payload.is_two_way_auth;
        }
        state.twoWayAuthError = null;
        state.successMessage = action.payload.message;
      })
      .addCase(toggleTwoWayAuth.rejected, (state, action) => {
        state.twoWayAuthUpdating = false;
        state.twoWayAuthError = action.payload;
        state.errorMessage = action.payload;
      });

    // Clear Cache
    builder
      .addCase(clearCache.pending, (state) => {
        state.cacheClearing = true;
        state.cacheError = null;
      })
      .addCase(clearCache.fulfilled, (state, action) => {
        state.cacheClearing = false;
        state.cacheError = null;
        state.successMessage = action.payload.message;
      })
      .addCase(clearCache.rejected, (state, action) => {
        state.cacheClearing = false;
        state.cacheError = action.payload;
        state.errorMessage = action.payload;
      });

    // Fetch Follow-up Templates
    builder
      .addCase(fetchFollowUpTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchFollowUpTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.followUpTemplates = action.payload;
        state.templatesError = null;
      })
      .addCase(fetchFollowUpTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.payload;
        state.errorMessage = action.payload;
      });

    // Save Follow-up Template
    builder
      .addCase(saveFollowUpTemplate.pending, (state) => {
        state.templateSaving = true;
        state.templateError = null;
      })
      .addCase(saveFollowUpTemplate.fulfilled, (state, action) => {
        state.templateSaving = false;
        state.templateError = null;
        state.successMessage = action.payload.message;
        // Refresh templates list
        state.templatesLoading = true;
      })
      .addCase(saveFollowUpTemplate.rejected, (state, action) => {
        state.templateSaving = false;
        state.templateError = action.payload;
        state.errorMessage = action.payload;
      });

    // Delete Follow-up Template
    builder
      .addCase(deleteFollowUpTemplate.pending, (state) => {
        state.templateDeleting = true;
        state.templateError = null;
      })
      .addCase(deleteFollowUpTemplate.fulfilled, (state, action) => {
        state.templateDeleting = false;
        state.templateError = null;
        state.followUpTemplates = state.followUpTemplates.filter(
          template => template.id !== action.payload.templateId
        );
        state.successMessage = action.payload.message;
      })
      .addCase(deleteFollowUpTemplate.rejected, (state, action) => {
        state.templateDeleting = false;
        state.templateError = action.payload;
        state.errorMessage = action.payload;
      });

    // Fetch Template Content
    builder
      .addCase(fetchTemplateContent.fulfilled, (state, action) => {
        // This is handled in the component
      })
      .addCase(fetchTemplateContent.rejected, (state, action) => {
        state.errorMessage = action.payload;
      });

    // Calendar Permissions
    builder
      .addCase(fetchCalendarPermissions.pending, (state) => {
        state.permissionsLoading = true;
        state.permissionsError = null;
      })
      .addCase(fetchCalendarPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.calendarPermissions = action.payload;
        state.permissionsError = null;
      })
      .addCase(fetchCalendarPermissions.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.permissionsError = action.payload;
        state.errorMessage = action.payload;
      });

    // Create Calendar Permission
    builder
      .addCase(createCalendarPermission.pending, (state) => {
        state.permissionSaving = true;
        state.permissionError = null;
      })
      .addCase(createCalendarPermission.fulfilled, (state, action) => {
        state.permissionSaving = false;
        state.permissionError = null;
        state.successMessage = action.payload.message;
        // Refresh permissions list
        state.permissionsLoading = true;
      })
      .addCase(createCalendarPermission.rejected, (state, action) => {
        state.permissionSaving = false;
        state.permissionError = action.payload;
        state.errorMessage = action.payload;
      });

    // Update Calendar Permission
    builder
      .addCase(updateCalendarPermission.pending, (state) => {
        state.permissionSaving = true;
        state.permissionError = null;
      })
      .addCase(updateCalendarPermission.fulfilled, (state, action) => {
        state.permissionSaving = false;
        state.permissionError = null;
        state.successMessage = action.payload.message;
        // Refresh permissions list
        state.permissionsLoading = true;
      })
      .addCase(updateCalendarPermission.rejected, (state, action) => {
        state.permissionSaving = false;
        state.permissionError = action.payload;
        state.errorMessage = action.payload;
      });

    // Delete Calendar Permission
    builder
      .addCase(deleteCalendarPermission.pending, (state) => {
        state.permissionDeleting = true;
        state.permissionError = null;
      })
      .addCase(deleteCalendarPermission.fulfilled, (state, action) => {
        state.permissionDeleting = false;
        state.permissionError = null;
        state.calendarPermissions = state.calendarPermissions.filter(
          permission => permission.id !== action.payload.permissionId
        );
        state.successMessage = action.payload.message;
      })
      .addCase(deleteCalendarPermission.rejected, (state, action) => {
        state.permissionDeleting = false;
        state.permissionError = action.payload;
        state.errorMessage = action.payload;
      });

    // Fetch Caregivers for Permission
    builder
      .addCase(fetchCaregiversForPermission.pending, (state) => {
        state.caregiversLoading = true;
        state.caregiversError = null;
      })
      .addCase(fetchCaregiversForPermission.fulfilled, (state, action) => {
        state.caregiversLoading = false;
        state.caregivers = action.payload;
        state.caregiversError = null;
      })
      .addCase(fetchCaregiversForPermission.rejected, (state, action) => {
        state.caregiversLoading = false;
        state.caregiversError = action.payload;
        state.errorMessage = action.payload;
      });
  }
});

export const { 
  setActiveTab, 
  clearMessages, 
  setSuccessMessage, 
  setErrorMessage 
} = settingsSlice.actions;

export default settingsSlice.reducer;
